import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Zap, LayoutDashboard, Target, FileText, TrendingUp, PenTool,
  CreditCard, Settings, LogOut, Menu, X, ChevronRight, ShieldCheck, Mic
} from "lucide-react";
import FeedbackButton from "@/components/FeedbackButton";

const NAV_ITEMS = [
  { label: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { label: "Application Analyzer", path: "/ats", icon: Target },
  { label: "NHS SI Generator", path: "/nhs-generator", icon: FileText },
  { label: "Band Coaching", path: "/band-coaching", icon: TrendingUp },
  { label: "CV Rewriter", path: "/cv-rewriter", icon: PenTool },
  { label: "Interview Sim", path: "/interview", icon: Mic },
  { label: "Settings", path: "/settings", icon: Settings },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const buildVersion = process.env.REACT_APP_BUILD_VERSION;
  const shortBuild = buildVersion ? buildVersion.slice(0, 7) : "not-set";
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const planBadge = {
    free: { text: "Free", className: "bg-slate-100 text-slate-600" },
    pro: { text: "Pro", className: "bg-[#003087]/10 text-[#003087]" },
    premium: { text: "Premium", className: "bg-[#00A3A6]/10 text-[#00A3A6]" },
  };
  const badge = planBadge[user?.subscription_plan] || planBadge.free;

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Mobile header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-slate-200 lg:hidden">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[#003087] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
          </div>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-btn">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-2 px-6 py-5">
            <div className="w-8 h-8 rounded-md bg-[#003087] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
          </div>
          <Separator />

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-[#003087] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}
                  data-testid={`nav-${item.path.replace("/", "")}`}
                >
                  <item.icon className="w-4.5 h-4.5" />
                  {item.label}
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          <Separator />
          <div className="px-3 py-3 space-y-1">
            <Link
              to="/pricing"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === "/pricing" ? "bg-[#003087] text-white" : "text-slate-600 hover:bg-slate-100"}`}
              data-testid="nav-pricing"
            >
              <CreditCard className="w-4.5 h-4.5" />
              Subscription
            </Link>
            {user?.is_admin && (
              <Link
                to="/admin"
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${location.pathname === "/admin" ? "bg-[#003087] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid="nav-admin"
              >
                <ShieldCheck className="w-4.5 h-4.5" />
                Admin
              </Link>
            )}
          </div>

          {/* User section */}
          <div className="px-4 py-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#003087] flex items-center justify-center text-white text-sm font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name || "User"}</p>
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${badge.className}`}>{badge.text}</span>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-slate-500 hover:text-red-600 hover:bg-red-50 text-sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-10 max-w-7xl mx-auto">
          {children}

          <div className="mt-10 text-xs text-slate-400 text-center" data-testid="build-version-dashboard">
            Build: {shortBuild}
          </div>

        </div>
      </main>

      <FeedbackButton />
    </div>
  );
}
